// Coming back to a page the browser kept alive.
//
// Leaving for another site (the payment provider's checkout or billing page, a
// sign-in provider) starts with the full-screen loader up, and the browser then
// navigates away. Pressing Back can bring the page out of the back/forward cache
// exactly as it was left: loader up, spinners spinning, and the plan or billing
// state it shows already out of date. A fresh load of the return url never has
// this problem, so only a restore (pageshow with `persisted`) is handled here.

/** sessionStorage key: what the page was left for, set right before leaving. */
export const LEFT_FOR_KEY = "bunnyquery:left_for";

export type BackForwardOptions = {
    win?: Window;
    storage?: Pick<Storage, "getItem" | "setItem" | "removeItem"> | null;
    /** Reload the page: what a restore does when the page was left for billing. */
    reload?: () => void;
    /** Drop the full-screen loader: what any other restore does. */
    removeLoader?: () => void;
};

function storageOf(opts: BackForwardOptions): BackForwardOptions["storage"] {
    if (opts.storage !== undefined) return opts.storage;
    try {
        return (opts.win || window).sessionStorage;
    } catch {
        return null; // storage blocked: the flag is simply never set
    }
}

/** Remember that the page is leaving for `what` (for example "billing"). */
export function markLeavingFor(what: string, opts: BackForwardOptions = {}): void {
    try {
        storageOf(opts)?.setItem(LEFT_FOR_KEY, what);
    } catch {
        // storage blocked
    }
}

/**
 * Handle a back/forward cache restore. Returns what it did, for the tests.
 * - Left for billing, restored: the plan or billing state on screen is stale, and
 *   the page reloads (the same thing the subscription page does after any change).
 * - Any other restore: the loader that was up when the page was left comes down.
 * - A fresh load (not persisted) only clears the flag, so a Back much later, from
 *   some unrelated site, does not reload.
 */
export function onPageShow(persisted: boolean, opts: BackForwardOptions = {}): "reload" | "remove-loader" | "none" {
    const storage = storageOf(opts);
    let leftFor: string | null = null;
    try {
        leftFor = storage?.getItem(LEFT_FOR_KEY) ?? null;
        if (leftFor) storage?.removeItem(LEFT_FOR_KEY);
    } catch {
        leftFor = null;
    }
    if (!persisted) return "none";
    if (leftFor) {
        (opts.reload || (() => (opts.win || window).location.reload()))();
        return "reload";
    }
    opts.removeLoader?.();
    return "remove-loader";
}

export function installBackForwardRestore(opts: BackForwardOptions = {}): void {
    const win = opts.win || window;
    win.addEventListener("pageshow", (event) => {
        onPageShow(!!(event as PageTransitionEvent).persisted, opts);
    });
}
